import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import SiteListTable from 'src/components/sites/SiteListTable';
import NewSite from 'src/components/sites/NewSite';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';
import ListRefreshButton from 'src/common/ListRefreshButton';
import { useHistory } from 'react-router';
import ROUTE from 'src/utils/routeConstants';
import ColumnSearch from 'src/common/ColumnSearch';

const SiteList = ({ location }) => {
  const history = useHistory();
  const { search, setSearch } = usePersistentListFilters();
  const theme = useTheme();
  const { getUser } = useAuth();
  const isAdmin = isAdminUser(getUser);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [searchText, setSearchText] = useState(search);
  const [searchFilters, setSearchFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSearch = (e) => { setSearchText(e.target.value); setSearch(e.target.value); };

  const handleViewSite = (site) => {
    history.push(`${ROUTE.sites}/${site.id}`, { viewOnly: true });
  };

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setOpenModal(true);
  };

  const handleCloseEdit = () => {
    setOpenModal(false);
    setSelectedSite(null);
  };

  return (
    <>
      <Helmet>
        <title>Sites | Retrack FMS</title>
      </Helmet>
      <Box
        className="transaction-style-page transaction-style-duplicate-header"
        sx={{
          backgroundColor: '#FFFFFF', // Clean White Canvas
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* --- Unified Header Section --- */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{
              py: 2,
              mb: 2,
              flexShrink: 0
            }}
          >
            {/* Left: Title & Subtitle */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                SITES
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Manage fuel stations, assets, and operational status.
              </Typography>
            </Box>

            {/* Right: Actions (Search + Button merged) */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{
                width: { xs: '100%', md: 'auto' },
                flex: { md: 1 },
                minWidth: 0
              }}
            >
              <ColumnSearch
                fields={[
                  { key: 'code', label: 'Site Code', defaultChecked: true },
                  { key: 'name', label: 'Site Name' }
                ]}
                values={searchFilters}
                onApply={(values) => {
                  setSearchFilters(values);
                  handleSearch({ target: { value: '' } });
                }}
                onClear={() => {
                  setSearchFilters({});
                  handleSearch({ target: { value: '' } });
                }}
                containerSx={{
                  width: { xs: '100%', sm: 320 },
                  flexShrink: 0
                }}
                sx={{ width: { xs: '100%', sm: 320 } }}
              />
              <ListRefreshButton onRefresh={() => setRefreshKey((value) => value + 1)} />

              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    ml: { md: 'auto' },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: theme.palette.primary.main, // Use Brand Primary Color
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  Add Site
                </Button>
              )}
            </Stack>
          </Stack>

          {/* --- Content Section --- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <SiteListTable
              refreshData={refreshKey || location.state?.fromDashboard}
              searchText={searchText}
              searchFilters={searchFilters}
              onViewSite={handleViewSite}
              onEditSite={handleEditSite}
            />
          </Box>

          <NewSite
            key={selectedSite?.id || 'new-site'}
            open={openModal}
            initialValues={selectedSite}
            handleClose={handleCloseEdit}
            onSaved={() => setRefreshKey((value) => value + 1)}
          />
        </Container>
      </Box>
    </>
  );
};

SiteList.propTypes = {
  location: PropTypes.object
};

export default SiteList;
